import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ComponentRenderMode } from '../../../types/component-skin';
import { adaptFixedBgTextProps } from './FixedBgText.adapter';
import { HtmlContent } from './FixedBgText.types';
import { useTranslation } from '../../../redux/TranslationHelper';

/**
 * FixedBgText 컴포넌트 비즈니스 로직 훅
 */
export function useFixedBgTextLogic(componentData: any, mode: ComponentRenderMode) {
  // 어댑터를 통해 props 변환
  const adaptedProps = useMemo(() => adaptFixedBgTextProps(componentData), [componentData]);
  
  // 속성 및 상태 추출
  const { 
    id, 
    style = {}, 
    componentProps = {}, 
    content = {},
    imageUrl,
    title,
    description,
    debug = false,
    viewMode,
    isPreview
  } = adaptedProps;
  
  // 훅 초기화
  const { t } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(true);
  const [hasError, setHasError] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  
  // 이미지 URL 처리
  const finalImageUrl = useMemo(() => {
    return componentProps?.bgImageUrl || imageUrl || (typeof content === 'object' ? content.imageUrl : '') || '';
  }, [componentProps?.bgImageUrl, imageUrl, content]);
  
  // 제목과 설명 처리
  const finalTitle = useMemo(() => title || (typeof content === 'object' ? content.title : '') || '', [title, content]);
  const finalDescription = useMemo(() => description || (typeof content === 'object' ? content.description : '') || '', [description, content]);
  
  // HTML 콘텐츠 처리 함수 (메모이제이션)
  const getHtmlContent = useCallback(() => {
    let htmlContent = '';
    
    // content가 문자열인 경우 (단순 HTML)
    if (typeof content === 'string') {
      htmlContent = content;
    }
    
    // content가 객체인 경우 (디바이스별 속성 또는 HTML 속성 확인)
    if (content && typeof content === 'object') {
      const contentObj = content as HtmlContent;
      // deviceProperty가 있는 경우 (디바이스별 설정)
      if (contentObj.deviceProperty) {
        const isMobile = viewMode === 'mobile' || viewMode === 'tablet';
        
        if (isMobile && contentObj.deviceProperty.mobile) {
          // 모바일이면서 모바일 속성이 있으면 모바일 속성 사용
          htmlContent = contentObj.deviceProperty.mobile;
        } else if (!isMobile && contentObj.deviceProperty.pc) {
          // PC이면서 PC 속성이 있으면 PC 속성 사용
          htmlContent = contentObj.deviceProperty.pc;
        } else if (contentObj.deviceProperty.pc) {
          // 기본값으로 PC 속성 사용
          htmlContent = contentObj.deviceProperty.pc;
        }
      }
      
      // html 속성이 있는 경우
      if (contentObj.html) {
        htmlContent = contentObj.html;
      }
    }
    
    // 기본값으로 빈 문자열 반환
    return htmlContent || '';
  }, [content, viewMode]);
  
  // HTML 콘텐츠 계산 (메모이제이션)
  const htmlContent = useMemo(() => getHtmlContent(), [getHtmlContent]);
  
  // 이미지 로드 처리
  useEffect(() => {
    if (!finalImageUrl) return;
    
    try {
      // 이미지 URL이 실제로 접근 가능한지 확인
      const img = new Image();
      
      img.onload = () => {
        setIsLoaded(true);
        setHasError(false);
        setBackgroundImageUrl(finalImageUrl);
      };
      
      img.onerror = () => {
        console.error(t('이미지 로드 실패:'), finalImageUrl);
        setIsLoaded(true);
        setHasError(true);
      };
      
      // 이미지 로드 시작
      img.src = finalImageUrl;
      
      // 이미 캐시된 이미지인 경우
      if (img.complete) {
        setIsLoaded(true);
        setHasError(false);
        setBackgroundImageUrl(finalImageUrl);
      }
    } catch (error) {
      console.error(t('이미지 로드 중 오류 발생:'), error);
      setIsLoaded(true);
      setHasError(true);
    }
  }, [finalImageUrl, t]);
  
  // 오버레이 투명도 설정
  const overlayOpacity = componentProps?.overlayOpacity !== undefined ? componentProps.overlayOpacity : 0.5;
  const overlayColor = componentProps?.overlayColor || 'rgba(0, 0, 0, 1)';
  
  // RGBA 색상 생성 함수
  const getRgbaColor = useCallback((color: string, opacity: number) => {
    if (color.startsWith('rgba')) {
      // 이미 rgba 형식이면 그대로 사용하되 불투명도만 변경
      return color.replace(/rgba\([^,]+,[^,]+,[^,]+,\s*[\d.]+\)/, `rgba($1,$2,$3,${opacity})`);
    } else if (color.startsWith('rgb')) {
      // rgb 형식이면 rgba로 변환
      return color.replace(/rgb\(([^)]+)\)/, `rgba($1,${opacity})`);
    } else if (color.startsWith('#')) {
      // HEX 형식이면 RGB 값으로 변환 후 RGBA 생성
      let r = 0, g = 0, b = 0;
      
      // #RGB 또는 #RRGGBB 형식 처리
      if (color.length === 4) {
        r = parseInt(color[1] + color[1], 16);
        g = parseInt(color[2] + color[2], 16);
        b = parseInt(color[3] + color[3], 16);
      } else if (color.length === 7) {
        r = parseInt(color.substring(1, 3), 16);
        g = parseInt(color.substring(3, 5), 16);
        b = parseInt(color.substring(5, 7), 16);
      }
      
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    
    // 기본값으로 검은색 반투명 반환
    return `rgba(0, 0, 0, ${opacity})`;
  }, []);
  
  // 오버레이 스타일 생성 (메모이제이션)
  const overlayStyle = useMemo(() => ({
    backgroundColor: getRgbaColor(overlayColor, overlayOpacity)
  }), [getRgbaColor, overlayColor, overlayOpacity]);
  
  // 콘텐츠 정렬 설정
  const contentAlignment = componentProps?.contentAlignment || 'center';
  const contentStyle = useMemo(() => ({
    justifyContent: contentAlignment === 'top' ? 'flex-start' : 
                    contentAlignment === 'bottom' ? 'flex-end' : 'center'
  }), [contentAlignment]);
  
  // 텍스트 색상 설정
  const textColor = componentProps?.textColor || '#ffffff';
  const htmlContentStyle = useMemo(() => ({
    color: textColor
  }), [textColor]);
  
  // 높이 설정
  const isMobile = viewMode === 'mobile' || viewMode === 'tablet';
  const height = componentProps?.height || (isMobile ? 400 : 500);
  
  // 루트 컴포넌트 스타일 (CSS 변수 포함) (메모이제이션)
  const rootStyle = useMemo(() => ({
    ...style,
    minHeight: `${height}px`,
    backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : 'none',
    backgroundPosition: 'center center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    '--text-color': textColor,
    opacity: 1 // 항상 보이도록 설정
  }), [style, height, textColor, backgroundImageUrl]);
  
  // 디버그용 데이터 속성
  const dataAttributes = {
    'data-loaded': isLoaded ? 'true' : 'false',
    'data-has-error': hasError ? 'true' : 'false',
    'data-debug': debug ? 'true' : 'false'
  };

  // 스킨 시스템에 맞는 data/actions 구조로 반환
  return {
    data: {
      id,
      style,
      componentProps,
      content,
      imageUrl,
      title,
      description,
      debug,
      viewMode,
      isPreview,
      isLoaded,
      hasError,
      componentRef,
      finalImageUrl,
      finalTitle,
      finalDescription,
      htmlContent,
      overlayStyle,
      contentStyle,
      textColor,
      htmlContentStyle,
      rootStyle,
      dataAttributes,
      height,
      overlayOpacity,
      overlayColor,
      contentAlignment,
      t
    },
    actions: {
      // 현재 FixedBgText는 상호작용이 없는 정적 컴포넌트
      // 필요시 향후 이벤트 핸들러 추가 가능
    }
  };
}

export default useFixedBgTextLogic;